import { Controller } from "@hotwired/stimulus"

const TIME_STEP_MINUTES = 15

export default class extends Controller {
  static targets = [
    "startsAtInput",
    "endsAtInput",
    "dateInput",
    "startTimeInput",
    "endTimeInput",
    "calendarPanel",
    "calendarTitle",
    "calendarGrid",
    "timePanel",
    "timeList"
  ]

  static values = {
    range: { type: Boolean, default: true },
    selectedDate: String,
    startMinutes: Number,
    endMinutes: Number,
    translations: Object,
    datePlaceholder: String,
    startTimePlaceholder: String,
    endTimePlaceholder: String
  }

  connect () {
    this.activeTimeField = null
    this.viewMonth = this.initialViewMonth()
    this.boundClickOutside = this.clickOutside.bind(this)
    this.parseFromHiddenInputs()
    this.renderCalendar()
    this.updateVisibleInputs()
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect () {
    document.removeEventListener("click", this.boundClickOutside)
  }

  parseFromHiddenInputs () {
    const startValue = this.startsAtInputTarget.value

    if (startValue) {
      const startDate = new Date(startValue)
      if (!Number.isNaN(startDate.getTime())) {
        this.selectedDateValue = this.toIsoDate(startDate)
        this.startMinutesValue = startDate.getHours() * 60 + startDate.getMinutes()
      }
    }

    if (this.rangeValue && this.hasEndsAtInputTarget) {
      const endValue = this.endsAtInputTarget.value
      if (endValue) {
        const endDate = new Date(endValue)
        if (!Number.isNaN(endDate.getTime())) {
          this.endMinutesValue = endDate.getHours() * 60 + endDate.getMinutes()
        }
      }
    }

    if (!this.selectedDateValue) {
      this.selectedDateValue = this.toIsoDate(new Date())
    }

    this.syncHiddenInputs()
  }

  initialViewMonth () {
    if (this.selectedDateValue) {
      const parts = this.selectedDateValue.split("-").map(Number)
      if (parts.length === 3) return new Date(parts[0], parts[1] - 1, 1)
    }

    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), 1)
  }

  openCalendar (event) {
    event.stopPropagation()
    this.closeTimePanel()
    this.calendarPanelTarget.classList.remove("hidden")
    this.positionPanel(this.calendarPanelTarget, this.dateInputTarget)
    this.renderCalendar()
  }

  keepFocus (event) {
    event.preventDefault()
  }

  openStartTime (event) {
    event.stopPropagation()
    this.openTimePanel("start", this.startTimeInputTarget)
  }

  openEndTime (event) {
    event.stopPropagation()
    if (!this.rangeValue || !this.hasEndTimeInputTarget) return

    this.openTimePanel("end", this.endTimeInputTarget)
  }

  openTimePanel (field, anchor) {
    this.activeTimeField = field
    this.closeCalendar()
    this.positionPanel(this.timePanelTarget, anchor)
    this.renderTimeList({ filterQuery: "", scrollToSelected: true })
    this.timePanelTarget.classList.remove("hidden")
  }

  handleStartTimeInput () {
    this.filterActiveTimeList(this.startTimeInputTarget)
  }

  handleEndTimeInput () {
    if (!this.rangeValue || !this.hasEndTimeInputTarget) return

    this.filterActiveTimeList(this.endTimeInputTarget)
  }

  filterActiveTimeList (anchor) {
    const field = anchor === this.startTimeInputTarget ? "start" : "end"
    if (this.activeTimeField !== field) {
      this.openTimePanel(field, anchor)
    }

    this.renderTimeList({
      filterQuery: anchor.value,
      scrollToSelected: false
    })
  }

  commitDate () {
    const raw = this.dateInputTarget.value.trim()
    if (!raw) return

    if (this.normalizedDateLabel(raw) === this.normalizedDateLabel(this.formatDateLabel(this.selectedDateValue))) {
      this.closeCalendar()
      return
    }

    const parsed = this.parseDateInput(raw)
    if (parsed) {
      this.selectedDateValue = parsed
      this.syncHiddenInputs()
    }
    this.updateVisibleInputs()
    this.closeCalendar()
  }

  commitStartTime () {
    const parsed = this.parseTimeInput(this.startTimeInputTarget.value)
    if (parsed !== null) {
      this.startMinutesValue = parsed
      this.syncHiddenInputs()
    }
    this.updateVisibleInputs()
    this.closeTimePanel()
  }

  commitEndTime () {
    if (!this.rangeValue || !this.hasEndTimeInputTarget) return

    const parsed = this.parseTimeInput(this.endTimeInputTarget.value)
    if (parsed !== null) {
      this.endMinutesValue = parsed
      this.syncHiddenInputs()
    }
    this.updateVisibleInputs()
    this.closeTimePanel()
  }

  handleDateKeydown (event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitDate()
    }
  }

  handleStartTimeKeydown (event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitStartTime()
    }
  }

  handleEndTimeKeydown (event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitEndTime()
    }
  }

  positionPanel (panel, anchor) {
    const rect = anchor.getBoundingClientRect()
    const containerRect = this.element.getBoundingClientRect()

    panel.style.left = `${rect.left - containerRect.left}px`
    panel.style.top = `${rect.bottom - containerRect.top + 4}px`
  }

  previousMonth (event) {
    event.stopPropagation()
    this.viewMonth.setMonth(this.viewMonth.getMonth() - 1)
    this.renderCalendar()
  }

  nextMonth (event) {
    event.stopPropagation()
    this.viewMonth.setMonth(this.viewMonth.getMonth() + 1)
    this.renderCalendar()
  }

  selectDate (event) {
    event.preventDefault()
    event.stopPropagation()

    this.selectedDateValue = event.currentTarget.dataset.date
    this.syncHiddenInputs()
    this.updateVisibleInputs()
    this.closeCalendar()
  }

  selectTime (event) {
    event.preventDefault()
    event.stopPropagation()

    const minutes = Number.parseInt(event.currentTarget.dataset.minutes, 10)
    if (Number.isNaN(minutes)) return

    if (this.activeTimeField === "start") {
      this.startMinutesValue = minutes
      if (this.rangeValue && this.endMinutesValue <= minutes) {
        this.endMinutesValue = Math.min(minutes + 60, (24 * 60) - 1)
      }
    } else {
      this.endMinutesValue = minutes
    }

    this.syncHiddenInputs()
    this.updateVisibleInputs()
    this.closeTimePanel()
  }

  clickOutside (event) {
    if (this.element.contains(event.target)) return

    this.commitDate()
    this.commitStartTime()
    this.commitEndTime()
    this.closeCalendar()
    this.closeTimePanel()
  }

  closeCalendar () {
    if (this.hasCalendarPanelTarget) this.calendarPanelTarget.classList.add("hidden")
  }

  closeTimePanel () {
    if (this.hasTimePanelTarget) this.timePanelTarget.classList.add("hidden")
    this.activeTimeField = null
  }

  renderCalendar () {
    if (!this.hasCalendarGridTarget) return

    const year = this.viewMonth.getFullYear()
    const month = this.viewMonth.getMonth()
    this.calendarTitleTarget.textContent = this.monthTitle(this.viewMonth)
    this.calendarGridTarget.replaceChildren(this.buildMonthGrid(year, month))
  }

  renderTimeList ({ filterQuery = "", scrollToSelected = true } = {}) {
    if (!this.hasTimeListTarget) return

    const selectedMinutes = this.activeTimeField === "start" ? this.startMinutesValue : this.endMinutesValue
    const selectedStep = this.nearestStep(selectedMinutes)
    const fragment = document.createDocumentFragment()
    let firstMatchMinutes = null

    for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
      const label = this.formatTime(minutes)
      if (!this.timeMatchesQuery(label, filterQuery)) continue

      if (firstMatchMinutes === null) firstMatchMinutes = minutes

      const button = document.createElement("button")
      button.type = "button"
      button.dataset.minutes = String(minutes)
      button.dataset.action = "click->date-time-picker#selectTime"
      button.textContent = label
      button.className = "block w-full px-3 py-2 text-left text-sm text-copy-color-one hover:bg-background-light transition-colors"

      if (minutes === selectedStep) {
        button.className += " bg-button-primary-bg/10 text-button-primary-bg font-medium"
      }

      fragment.appendChild(button)
    }

    this.timeListTarget.replaceChildren(fragment)

    if (!scrollToSelected) {
      const firstButton = this.timeListTarget.querySelector("button")
      if (firstButton) firstButton.scrollIntoView({ block: "nearest" })
      return
    }

    const selectedButton = this.timeListTarget.querySelector(`[data-minutes="${selectedStep}"]`)
    if (selectedButton) {
      selectedButton.scrollIntoView({ block: "center" })
      return
    }

    if (firstMatchMinutes !== null) {
      const firstMatch = this.timeListTarget.querySelector(`[data-minutes="${firstMatchMinutes}"]`)
      if (firstMatch) firstMatch.scrollIntoView({ block: "nearest" })
    }
  }

  timeMatchesQuery (label, query) {
    const normalizedQuery = String(query || "").trim().toLowerCase().replace(/\s+/g, "")
    if (!normalizedQuery) return true

    const labelDigits = label.replace(/\D/g, "")
    const queryDigits = normalizedQuery.replace(/\D/g, "")

    if (label.startsWith(normalizedQuery)) return true
    if (queryDigits && labelDigits.startsWith(queryDigits)) return true

    return false
  }

  buildMonthGrid (year, month) {
    const wrap = document.createElement("div")
    wrap.className = "space-y-2 w-full"

    const daysRow = document.createElement("div")
    daysRow.className = "grid grid-cols-7 gap-1 mb-0.5 w-full"
    const dayNames = this.translationsValue.days || []
    dayNames.forEach((day) => {
      const cell = document.createElement("div")
      cell.className = "text-center text-[11px] font-semibold text-copy-color-two tabular-nums px-0.5"
      cell.textContent = day
      daysRow.appendChild(cell)
    })
    wrap.appendChild(daysRow)

    const grid = document.createElement("div")
    grid.className = "grid grid-cols-7 gap-1 w-full"
    grid.appendChild(this.buildDayCells(year, month))
    wrap.appendChild(grid)

    return wrap
  }

  buildDayCells (year, month) {
    const fragment = document.createDocumentFragment()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const startingDay = new Date(year, month, 1).getDay()
    const mondayBasedStartingDay = startingDay === 0 ? 6 : startingDay - 1
    const todayIso = this.toIsoDate(new Date())

    for (let i = 0; i < mondayBasedStartingDay; i++) {
      fragment.appendChild(document.createElement("div"))
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      fragment.appendChild(this.buildDayButton(iso, day, todayIso))
    }

    return fragment
  }

  buildDayButton (iso, day, todayIso) {
    const button = document.createElement("button")
    button.type = "button"
    button.dataset.date = iso
    button.dataset.action = "click->date-time-picker#selectDate"
    button.textContent = String(day)

    const isSelected = iso === this.selectedDateValue
    const isToday = iso === todayIso

    button.className =
      "w-full aspect-square max-h-9 flex items-center justify-center rounded-full text-sm font-semibold tabular-nums transition-colors border border-transparent cursor-pointer"

    if (isSelected) {
      button.className +=
        " bg-button-primary-bg text-button-primary-text hover:bg-button-primary-bg shadow-sm"
    } else {
      button.className +=
        " bg-background-light/70 text-copy-color-one hover:bg-background-light hover:border-button-primary-bg/40"

      if (isToday) {
        button.className += " ring-1 ring-button-primary-bg/70 font-bold text-button-primary-bg"
      }
    }

    return button
  }

  monthTitle (date) {
    const months = this.translationsValue.months || []
    const name = months[date.getMonth()] || ""
    return `${name} ${date.getFullYear()}`
  }

  updateVisibleInputs () {
    if (this.hasDateInputTarget) {
      this.dateInputTarget.value = this.formatDateLabel(this.selectedDateValue)
    }

    if (this.hasStartTimeInputTarget) {
      this.startTimeInputTarget.value = this.formatTime(this.startMinutesValue)
    }

    if (this.rangeValue && this.hasEndTimeInputTarget) {
      this.endTimeInputTarget.value = this.formatTime(this.endMinutesValue)
    }
  }

  syncHiddenInputs () {
    const startDate = this.buildDateTime(this.selectedDateValue, this.startMinutesValue)

    this.startsAtInputTarget.value = this.toDatetimeLocal(startDate)
    this.dispatchDomChange(this.startsAtInputTarget)

    if (this.rangeValue && this.hasEndsAtInputTarget) {
      const endDate = this.buildDateTime(this.selectedDateValue, this.endMinutesValue)
      this.endsAtInputTarget.value = this.toDatetimeLocal(endDate)
      this.dispatchDomChange(this.endsAtInputTarget)
    }
  }

  // Use CustomEvent so domain renames (Event ↔ Lesson) cannot break DOM dispatch.
  dispatchDomChange (element) {
    element.dispatchEvent(new CustomEvent("change", { bubbles: true }))
    element.dispatchEvent(new CustomEvent("input", { bubbles: true }))
  }

  buildDateTime (isoDate, minutes) {
    const [year, month, day] = isoDate.split("-").map(Number)
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return new Date(year, month - 1, day, hours, mins, 0, 0)
  }

  toDatetimeLocal (date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  toIsoDate (date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  formatDateLabel (isoDate) {
    if (!isoDate) return ""

    const [year, month, day] = isoDate.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    const weekday = new Intl.DateTimeFormat("es-CL", { weekday: "long" }).format(date)
    const monthName = new Intl.DateTimeFormat("es-CL", { month: "long" }).format(date)

    return `${this.capitalize(weekday)}, ${day} ${monthName}`
  }

  formatTime (minutes) {
    const hours24 = Math.floor(minutes / 60)
    const mins = minutes % 60

    return `${String(hours24).padStart(2, "0")}:${String(mins).padStart(2, "0")}`
  }

  parseDateInput (value) {
    const trimmed = value.trim()
    if (!trimmed) return null

    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (isoMatch) {
      return this.validIsoDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]))
    }

    const dmyMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
    if (dmyMatch) {
      return this.validIsoDate(Number(dmyMatch[3]), Number(dmyMatch[2]), Number(dmyMatch[1]))
    }

    return this.parseSpanishFormattedDate(trimmed)
  }

  parseSpanishFormattedDate (value) {
    const match = value.match(/^[^,]+,\s*(\d{1,2})\s+(.+)$/i)
    if (!match) return null

    const day = Number.parseInt(match[1], 10)
    const monthToken = this.normalizeDateLabel(match[2])
    const months = this.translationsValue.months || []
    const monthIndex = months.findIndex((month) => {
      const normalizedMonth = this.normalizeDateLabel(month)
      return normalizedMonth === monthToken ||
        normalizedMonth.startsWith(monthToken) ||
        monthToken.startsWith(normalizedMonth.slice(0, 3))
    })

    if (monthIndex === -1 || Number.isNaN(day)) return null

    const year = this.inferredYearForMonth()
    return this.validIsoDate(year, monthIndex + 1, day)
  }

  inferredYearForMonth () {
    if (this.selectedDateValue) {
      return Number.parseInt(this.selectedDateValue.split("-")[0], 10)
    }

    if (this.viewMonth) return this.viewMonth.getFullYear()

    return new Date().getFullYear()
  }

  normalizedDateLabel (value) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
  }

  parseTimeInput (value) {
    const trimmed = value.trim().toLowerCase().replace(/\s+/g, "")
    if (!trimmed) return null

    const match = trimmed.match(/^(\d{1,2})(?::|\.|-)?(\d{2})?(am|pm)?$/)
    if (!match) return null

    let hours = Number.parseInt(match[1], 10)
    const minutes = match[2] ? Number.parseInt(match[2], 10) : 0
    const period = match[3]

    if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59 || hours > 24) return null

    if (period === "pm" && hours < 12) hours += 12
    if (period === "am" && hours === 12) hours = 0
    if (!period && hours === 24 && minutes === 0) hours = 0
    if (hours >= 24 || (hours === 24 && minutes > 0)) return null

    return (hours * 60) + minutes
  }

  validIsoDate (year, month, day) {
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null

    return this.toIsoDate(date)
  }

  nearestStep (minutes) {
    return Math.round(minutes / TIME_STEP_MINUTES) * TIME_STEP_MINUTES
  }

  capitalize (value) {
    if (!value) return value
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
}
